import { useState, type FormEvent } from "react";
import { Button } from "../../shared/ui/button.js";
import { RefreshButton } from "../../shared/ui/refresh-button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import {
  useAnalysisStatus,
  useAnalysisMonitors,
  useAnalysisMutation,
  type MonitorInput,
  type AnalysisMonitor,
} from "./queries.js";

import { action, money, timestamp } from "./analysis-format.js";
import { AnalysisActions } from "./analysis-actions.js";
import { AnalysisDeleteDialog } from "./analysis-report.js";

const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const blankMonitor: MonitorInput = {
  symbol: "",
  frequency: "weekly",
  weekDay: 5,
  time: "16:30",
  enabled: true,
};
function monitorInput(monitor: AnalysisMonitor): MonitorInput {
  return {
    symbol: monitor.symbol,
    frequency: monitor.frequency,
    weekDay: monitor.weekDay,
    time: monitor.time,
    enabled: monitor.enabled,
  };
}
export function AiAnalysisPage() {
  const status = useAnalysisStatus();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const monitors = useAnalysisMonitors(includeDeleted);
  const mutation = useAnalysisMutation();
  const [editing, setEditing] = useState<{ id?: string; input: MonitorInput } | null>(null);
  const [deletingId, setDeletingId] = useState("");
  const [notice, setNotice] = useState("");
  const pending = mutation.isPending;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (editing === null) return;
    mutation.mutate(
      {
        action: "save",
        body: { ...editing.input, symbol: editing.input.symbol.trim().toUpperCase() },
        ...(editing.id === undefined ? {} : { id: editing.id }),
      },
      {
        onSuccess: () => {
          setEditing(null);
          setNotice("Monitor saved.");
        },
      },
    );
  };

  return (
    <section className="page-stack page-stack--compact" aria-label="AI Analysis">
      <div className="page-toolbar">
        <h1>AI Analysis</h1>
        <div className="button-row">
          <RefreshButton queries={[status, monitors]} />
          <Button
            onClick={() => {
              mutation.reset();
              setEditing({ input: { ...blankMonitor } });
            }}
          >
            Add Monitor
          </Button>
        </div>
      </div>
      <p className="section-description">
        Scheduled stock research with conditional entry and holding recommendations. Analysis never
        places orders.
      </p>
      <label className="check-field">
        <input
          type="checkbox"
          checked={includeDeleted}
          onChange={(event) => setIncludeDeleted(event.currentTarget.checked)}
        />
        <span>Include deleted schedules</span>
      </label>
      {status.data === undefined ? (
        status.isError ? (
          <p role="alert">{status.error.message}</p>
        ) : (
          <p role="status">Loading analysis configuration…</p>
        )
      ) : (
        <div className="content-card ai-analysis-budget">
          <div>
            <strong>{status.data.enabled ? status.data.model : "Analysis is disabled"}</strong>
            <p className="section-description">
              {status.data.enabled
                ? "Market, company and economic research · 5–20 trading-day horizon"
                : "An administrator can configure AI analysis in Admin. A server API key, model, pricing and positive monthly budget are required."}
            </p>
          </div>
          <div>
            <strong>
              {money(status.data.usedBudgetUsd)} / ${status.data.monthlyBudgetUsd.toFixed(2)}
            </strong>
            <p className="section-description">
              {status.data.budgetMonth} UTC monthly budget · includes reserved costs
            </p>
            <small>
              Maximum reservation per analysis: {money(status.data.maxCostPerAnalysisUsd)}
            </small>
          </div>
        </div>
      )}
      {mutation.error === null ? null : (
        <p className="form-message--error" role="alert">
          {mutation.error.message}
        </p>
      )}
      {notice === "" ? null : <p role="status">{notice}</p>}
      {editing === null ? null : (
        <form className="content-card form-stack" onSubmit={submit} aria-label="Monitor schedule">
          <h2>{editing.id === undefined ? "Add stock monitor" : "Edit stock monitor"}</h2>
          <div className="field-grid ai-analysis-fields">
            <div className="field-stack">
              <label htmlFor="ai-symbol">Stock symbol</label>
              <input
                id="ai-symbol"
                required
                autoFocus
                maxLength={15}
                pattern="[A-Za-z][A-Za-z0-9.\-]{0,14}"
                placeholder="AAPL"
                value={editing.input.symbol}
                onChange={(event) =>
                  setEditing({
                    ...editing,
                    input: { ...editing.input, symbol: event.currentTarget.value.toUpperCase() },
                  })
                }
              />
            </div>
            <div className="field-stack">
              <label htmlFor="ai-frequency">Frequency</label>
              <select
                id="ai-frequency"
                value={editing.input.frequency}
                onChange={(event) =>
                  setEditing({
                    ...editing,
                    input: {
                      ...editing.input,
                      frequency: event.currentTarget.value as MonitorInput["frequency"],
                    },
                  })
                }
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            {editing.input.frequency !== "weekly" ? null : (
              <div className="field-stack">
                <label htmlFor="ai-weekday">Analysis day</label>
                <select
                  id="ai-weekday"
                  value={editing.input.weekDay}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      input: { ...editing.input, weekDay: Number(event.currentTarget.value) },
                    })
                  }
                >
                  {weekdays.map((day, index) => (
                    <option key={day} value={index + 1}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="field-stack">
              <label htmlFor="ai-time">Time (New York)</label>
              <input
                type="time"
                id="ai-time"
                required
                min="16:15"
                max="22:59"
                value={editing.input.time}
                onChange={(event) =>
                  setEditing({
                    ...editing,
                    input: { ...editing.input, time: event.currentTarget.value },
                  })
                }
              />
            </div>
          </div>
          <p className="field-hint">
            Daily runs follow trading sessions. Weekly holidays use the preceding session in that
            week. Monthly runs use the last trading session. All schedules use New York time.
          </p>
          <label className="check-field">
            <input
              type="checkbox"
              checked={editing.input.enabled}
              onChange={(event) =>
                setEditing({
                  ...editing,
                  input: { ...editing.input, enabled: event.currentTarget.checked },
                })
              }
            />
            <span>Schedule enabled</span>
          </label>
          <div className="button-row">
            <Button type="submit" disabled={pending}>
              {pending ? "Saving…" : "Save Monitor"}
            </Button>
            <Button type="button" tone="quiet" disabled={pending} onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {monitors.isPending ? (
        <ResourceState
          kind="loading"
          title="Loading monitors"
          description="Retrieving your stock analysis schedules."
        />
      ) : monitors.isError ? (
        <ResourceState
          kind="error"
          title="Monitors unavailable"
          description={monitors.error.message}
        />
      ) : monitors.data?.items.length === 0 ? (
        <ResourceState
          kind="empty"
          title="No stock monitors yet"
          description="Add a symbol and schedule to begin collecting AI analysis reports."
        />
      ) : (
        <div className="content-card ai-analysis-table">
          <table>
            <caption>Stock monitor schedules · New York time</caption>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Schedule</th>
                <th>Latest analysis</th>
                <th>Next analysis</th>
                <th>If not owned</th>
                <th>If owned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {monitors.data?.items.map((monitor) => (
                <tr key={monitor.id}>
                  <th scope="row">
                    {monitor.symbol}
                    <br />
                    <span className="status-pill">
                      {monitor.deleted === true ? "Deleted" : monitor.enabled ? "Active" : "Paused"}
                    </span>
                  </th>
                  <td data-label="Schedule">
                    {monitor.frequency === "weekly"
                      ? `Weekly · ${weekdays[monitor.weekDay - 1]}`
                      : monitor.frequency === "monthly"
                        ? "Monthly · last session"
                        : "Daily · trading sessions"}
                    <br />
                    <small>{monitor.time} Eastern</small>
                  </td>
                  <td data-label="Latest analysis">
                    {timestamp(
                      monitor.latestReport?.completedAt ?? monitor.latestReport?.createdAt,
                    )}
                    <br />
                    <small>
                      {monitor.latestReport?.status.replaceAll("_", " ") ?? "No reports"}
                    </small>
                  </td>
                  <td data-label="Next analysis">
                    {monitor.enabled ? timestamp(monitor.nextAnalysisAt) : "Paused"}
                  </td>
                  <td data-label="If not owned">
                    <span className="status-pill">
                      {action(monitor.latestReport?.decision?.entryAction)}
                    </span>
                  </td>
                  <td data-label="If owned">
                    <span className="status-pill">
                      {action(monitor.latestReport?.decision?.holdingAction)}
                    </span>
                  </td>
                  <td data-label="Actions">
                    <AnalysisActions symbol={monitor.symbol}>
                      <Button
                        tone="quiet"
                        disabled={
                          monitor.deleted === true ||
                          pending ||
                          status.data?.enabled !== true ||
                          monitor.latestReport?.status === "queued" ||
                          monitor.latestReport?.status === "running"
                        }
                        onClick={() =>
                          mutation.mutate(
                            { action: "run", id: monitor.id },
                            {
                              onSuccess: () => {
                                setNotice(
                                  `${monitor.symbol} analysis queued. Results appear when the worker completes.`,
                                );
                              },
                            },
                          )
                        }
                      >
                        Run Now
                      </Button>
                      <a
                        className="button button--quiet"
                        href={`/ai-analysis/${encodeURIComponent(monitor.id)}/history`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View History
                        <span aria-hidden="true"> ↗</span>
                      </a>
                      <Button
                        tone="quiet"
                        disabled={pending || monitor.deleted === true}
                        onClick={() => {
                          mutation.reset();
                          setEditing({ id: monitor.id, input: monitorInput(monitor) });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        tone="quiet"
                        disabled={pending || monitor.deleted === true}
                        onClick={() =>
                          mutation.mutate({
                            action: "save",
                            id: monitor.id,
                            body: { ...monitorInput(monitor), enabled: !monitor.enabled },
                          })
                        }
                      >
                        {monitor.enabled ? "Pause" : "Resume"}
                      </Button>
                      <Button
                        tone="danger"
                        disabled={pending || monitor.deleted === true}
                        onClick={() => setDeletingId(monitor.id)}
                      >
                        Delete schedule
                      </Button>
                    </AnalysisActions>
                    {deletingId !== monitor.id ? null : (
                      <AnalysisDeleteDialog
                        pending={pending}
                        onCancel={() => setDeletingId("")}
                        labelledBy="schedule-delete-title"
                        describedBy="schedule-delete-description"
                      >
                        <h3 id="schedule-delete-title">Delete {monitor.symbol} schedule?</h3>
                        <p id="schedule-delete-description">Existing reports are retained.</p>
                        {mutation.error === null ? null : (
                          <p role="alert">{mutation.error.message}</p>
                        )}
                        <div className="button-row">
                          <Button
                            tone="quiet"
                            autoFocus
                            disabled={pending}
                            onClick={() => setDeletingId("")}
                          >
                            Cancel
                          </Button>
                          <Button
                            tone="danger"
                            disabled={pending}
                            onClick={() =>
                              mutation.mutate(
                                { action: "delete", id: monitor.id },
                                {
                                  onSuccess: () => {
                                    setDeletingId("");
                                    setNotice("Monitor deleted. Saved reports are retained.");
                                  },
                                },
                              )
                            }
                          >
                            Confirm delete
                          </Button>
                        </div>
                      </AnalysisDeleteDialog>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
